/**
 * Admin action-requests queue tRPC hooks — the GB-team side of the
 * brand-side "Request a promotion" flow (Task 9's flat
 * admin.actionRequests* endpoints).
 */

import { trpc } from '../../lib/trpc';
import { downloadBase64 } from '../../utils/downloadBase64';

export function useAdminActionRequests() {
  return trpc.admin.actionRequestsList.useQuery(undefined, { staleTime: 30 * 1000 });
}

export function useResolveActionRequest() {
  const utils = trpc.useUtils();
  return trpc.admin.actionRequestsResolve.useMutation({
    onSuccess: () => {
      utils.admin.actionRequestsList.invalidate();
      utils.admin.actionRequestsPendingCount.invalidate();
    },
  });
}

export function useApproveActionRequest() {
  const utils = trpc.useUtils();
  return trpc.admin.actionRequestsApprove.useMutation({
    onSettled: () => {
      utils.admin.actionRequestsList.invalidate();
      utils.admin.actionRequestsPendingCount.invalidate();
    },
  });
}

/** Cancel a shipment notice filed in error; the server refuses once the warehouse pulled it. */
export function useAdminAsnCancel() {
  const utils = trpc.useUtils();
  return trpc.admin.actionRequestsAsnCancel.useMutation({
    onSuccess: () => {
      utils.admin.actionRequestsList.invalidate();
      utils.admin.actionRequestsPendingCount.invalidate();
    },
  });
}

/** Fetches the stored ASN CSV on click and hands it to the browser as a download. */
export function useAdminAsnCsvDownload() {
  const utils = trpc.useUtils();
  return async (requestId: string, asnId: string): Promise<void> => {
    const file = await utils.admin.actionRequestsAsnCsv.fetch({ requestId, asnId });
    downloadBase64(file.contentBase64, file.filename, 'text/csv');
  };
}

/** Fetches an offer-discount request's SKU list (style, marketplace, shopSku) as CSV on click. */
export function useAdminDiscountSkusDownload() {
  const utils = trpc.useUtils();
  return async (requestId: string): Promise<void> => {
    const file = await utils.admin.actionRequestsDiscountSkusCsv.fetch({ id: requestId });
    downloadBase64(file.contentBase64, file.filename, 'text/csv');
  };
}

/** Lightweight polled query for the sidebar badge (spec: polling, no websockets). */
export function usePendingActionRequestsCount() {
  return trpc.admin.actionRequestsPendingCount.useQuery(undefined, {
    refetchInterval: 60 * 1000,
  });
}
